import { t } from "../utils/translations/phrases";

/**
 * Spectating is 15 seconds behind live. After a death, pressing Watch plays a VHS-style "REWINDING" screen, then the
 * killer's view of the last seconds (you see yourself get killed). Watching from the lobby starts with a short
 * countdown instead (no rewind). A small badge always says how far behind live the view is.
 */
class RewindOverlayClass {
    private _root?: HTMLElement;
    private _badge?: HTMLElement;
    private _waiting?: HTMLElement;
    private _timer?: number;
    private _waitTimer?: number;
    private _over?: HTMLElement;
    private _endCard?: HTMLElement;
    private _overTimer?: number;
    /** the badge to show once the rewind animation ends (the game names whose view is replaying; set by play) */
    private _afterRewind = "";
    private _rewinding = false;

    private _build(): void {
        if (this._root) return;
        const root = document.createElement("div");
        root.id = "rewind-overlay";
        root.hidden = true;
        root.innerHTML = `
            <div class="rw-noise"></div>
            <div class="rw-center">
                <div class="rw-arrows">◀◀</div>
                <div class="rw-title">${t("REWINDING")}</div>
                <div class="rw-sub">${t("<seconds> SECONDS", { seconds: "<span class=\"rw-seconds\">15</span>" })}</div>
                <div class="rw-clock">0:00</div>
                <div class="rw-track"><div class="rw-fill"></div></div>
                <div class="rw-note">${t("Watching is always 15 seconds behind the live round")}</div>
            </div>`;
        const badge = document.createElement("div");
        badge.id = "rewind-badge";
        badge.hidden = true;
        const waiting = document.createElement("div");
        waiting.id = "watch-waiting";
        waiting.hidden = true;
        waiting.innerHTML = `
            <div class="ww-title">${t("WATCHING THE LIVE ROUND")}</div>
            <div class="ww-sub">${t("Everything you see is <b><seconds> seconds behind</b> live", { seconds: "<span class=\"ww-seconds\">15</span>" })}</div>
            <div class="ww-count">${t("Starting in <seconds>", { seconds: "<span class=\"ww-left\">15</span>" })}</div>`;
        // the match is over (told live), and this view, behind live, still plays its last moments
        const over = document.createElement("div");
        over.id = "match-over";
        over.hidden = true;
        over.setAttribute("role", "status");
        over.innerHTML = `
            <div class="mo-head"><span class="mo-title">${t("MATCH OVER")}</span><span class="mo-left">0:15</span></div>
            <div class="mo-sub">${t("You're <b><seconds> seconds behind live</b>: its last moments are still playing", { seconds: "<span class=\"mo-behind\">15</span>" })}</div>
            <div class="mo-track"><div class="mo-fill"></div></div>`;
        // the end reached the screen
        const endCard = document.createElement("div");
        endCard.id = "match-end";
        endCard.hidden = true;
        endCard.setAttribute("role", "dialog");
        endCard.setAttribute("aria-labelledby", "match-end-title");
        endCard.innerHTML = `
            <div class="me-title" id="match-end-title">${t("MATCH OVER")}</div>
            <div class="me-sub">${t("You watched it to the end, <seconds> seconds behind live.", { seconds: "<span class=\"me-behind\">15</span>" })}</div>
            <button type="button" class="me-menu">${t("Back to menu")}</button>`;
        document.body.append(root, badge, waiting, over, endCard);
        this._root = root;
        this._badge = badge;
        this._waiting = waiting;
        this._over = over;
        this._endCard = endCard;
    }

    /** the death rewind: the animation, then the replay badge */
    play(seconds: number, durationMs = 1600): void {
        this._build();
        const root = this._root!, badge = this._badge!;
        this.hideWaiting();
        this._afterRewind = t("◀◀ REPLAY · <seconds> S BEHIND LIVE", { seconds });
        this._rewinding = true;
        root.querySelector(".rw-seconds")!.textContent = String(seconds);
        badge.hidden = true;
        root.hidden = false;
        root.classList.remove("rw-out");
        void root.offsetWidth; // restart the CSS animations
        root.classList.add("rw-run");
        (root.querySelector(".rw-fill") as HTMLElement).style.animationDuration = `${durationMs}ms`;
        const clock = root.querySelector(".rw-clock")!;
        const started = performance.now();
        window.clearInterval(this._timer);
        this._timer = window.setInterval(() => {
            const k = Math.min(1, (performance.now() - started) / durationMs);
            clock.textContent = `-0:${String(Math.round(k * seconds)).padStart(2, "0")}`;
            if (k >= 1) {
                window.clearInterval(this._timer);
                root.classList.add("rw-out");
                window.setTimeout(() => { root.hidden = true; root.classList.remove("rw-run", "rw-out"); }, 280);
                this._rewinding = false;
                this.status(this._afterRewind);
            }
        }, 50);
    }

    /** the replay badge (whose view is replaying); shown once the rewind animation is over */
    replayLabel(text: string): void {
        this.status(text);
    }

    /** watching from the lobby: the feed is 15 s behind live, so it starts after a short countdown */
    waiting(seconds: number): void {
        this._build();
        const waiting = this._waiting!;
        waiting.querySelector(".ww-seconds")!.textContent = String(seconds);
        const left = waiting.querySelector(".ww-left")!;
        const ends = performance.now() + seconds * 1000;
        left.textContent = String(seconds);
        waiting.hidden = false;
        this._badge!.hidden = true;
        window.clearInterval(this._waitTimer);
        this._waitTimer = window.setInterval(() => {
            left.textContent = String(Math.max(0, Math.ceil((ends - performance.now()) / 1000)));
        }, 250);
    }

    hideWaiting(): void {
        window.clearInterval(this._waitTimer);
        if (this._waiting) this._waiting.hidden = true;
    }

    /** the small badge, always saying how far behind live the view is (during the rewind animation: once it ends) */
    status(text: string): void {
        this._build();
        this._afterRewind = text;
        if (this._rewinding) return;
        this._badge!.textContent = text;
        this._badge!.hidden = false;
    }

    /**
     * The match is over (the server said so live): this view is `behind` seconds behind live, so its last moments are
     * still playing; the end reaches the screen in `seconds`. A banner says so and counts down to it
     */
    matchOver(seconds: number, behind: number): void {
        this._build();
        const over = this._over;
        if (!over) return;
        const total = Math.max(1, seconds) * 1000;
        const ends = performance.now() + total;
        const behindText = over.querySelector(".mo-behind");
        if (behindText) behindText.textContent = String(behind);
        const left = over.querySelector(".mo-left");
        const fill = over.querySelector<HTMLElement>(".mo-fill");
        const show = (): void => {
            const ms = Math.max(0, ends - performance.now());
            if (left) left.textContent = `0:${String(Math.ceil(ms / 1000)).padStart(2, "0")}`;
            if (fill) fill.style.transform = `scaleX(${ms / total})`;
            if (ms <= 0) window.clearInterval(this._overTimer);
        };
        show();
        over.hidden = false;
        window.clearInterval(this._overTimer);
        this._overTimer = window.setInterval(show, 100);
    }

    /** the end of the match reached the screen (`behind` seconds behind live): the banner goes, the end card comes up (unless `card` is false) */
    ended(card: boolean, behind: number, onMenu: () => void): void {
        this._build();
        window.clearInterval(this._overTimer);
        if (this._over) this._over.hidden = true;
        const endCard = this._endCard;
        if (!card || !endCard) return;
        const behindText = endCard.querySelector(".me-behind");
        if (behindText) behindText.textContent = String(behind);
        const button = endCard.querySelector<HTMLButtonElement>(".me-menu");
        if (button) {
            button.onclick = () => {
                button.disabled = true;
                onMenu();
            };
            button.disabled = false;
        }
        endCard.hidden = false;
        button?.focus({ preventScroll: true });
    }

    /** the winner's card is the end screen (it came with the end) */
    hideEndCard(): void {
        if (this._endCard) this._endCard.hidden = true;
    }

    /** the match let this watcher go (its end was shown): the frozen view keeps its end card, the badge goes */
    stopped(): void {
        window.clearInterval(this._overTimer);
        if (this._over) this._over.hidden = true;
        if (this._badge) this._badge.hidden = true;
    }

    hide(): void {
        window.clearInterval(this._timer);
        window.clearInterval(this._overTimer);
        this._rewinding = false;
        this.hideWaiting();
        if (this._root) this._root.hidden = true;
        if (this._badge) this._badge.hidden = true;
        if (this._over) this._over.hidden = true;
        if (this._endCard) this._endCard.hidden = true;
    }
}

export const RewindOverlay = new RewindOverlayClass();
